import React, {useState, useEffect, useMemo} from 'react';
import ReactModal from "react-modal";
import './payAnnuityPeriod.scss';
import {HCheckbox, HInput} from "../../../../validation/HForm";
import {StatusPaymentAnnuityPeriod, viewReports} from "../../../../helpers/data-constants";
import InfoPanel from "../CloseModalLayout/InfoPanel";
import {FormProvider, useForm} from "react-hook-form";
import PaymentTypeSelector from "../../PaymentSelector/PaymentTypeSelector";
import {useDispatch, useSelector} from "react-redux";
import {
    changeStateAnnuityPeriod,
    clearModalError,
    getPaymentPeriodAnnuity, getPrintedForm,
    payAnnuityPeriod,
} from "../../../../redux/actions/contractAction";
import moment from "moment";
import LoaderLinear from "../../Loader/Loader";
import {formatDate, permissionsAccess, returnEndStr} from "../../../../helpers/common";
import {Message} from "../../index";
import {authService} from "../../../../services/auth-service";
import {ConfirmModal} from "../../../common";

const PayAnnuityPeriod = ({isOpen, onClose, dealId, dealNumber, item, updateData}) => {
    const {
        periodsPaymentAnnuityDeal,
        isSuccessAnnuityPeriod,
        modalError,
        isModalLoading,
        isDisabledButton,
        payPeriodAnnuity
    } = useSelector(({contract}) => contract)
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [paymentInputs, setPaymentInputs] = useState([]);
    const [paymentsValue, setPaymentsValue] = useState(0);
    const [amountValue, setAmountValue] = useState(0);
    const [change, setChange] = useState(0);
    const dispatch = useDispatch();
    const disableSubmitButton = !paymentInputs || !paymentsValue || isDisabledButton
    const dataActiveCashbox = authService.getActiveCashBoxId();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const form = useForm({
        mode: 'all',
        defaultValues: {
            deposited: 0,
            deposit: {
                cash: 0,
                nonCash: 0,
                bankTransaction: 0
            }
        },
    });

    const cashValue = form.watch('deposit.cash') || 0;
    const nonCashValue = form.watch('deposit.nonCash') || 0;
    const nonCashAccountValue = form.watch('deposit.nonCashAccount') || 0;
    const bankTransactionValue = form.watch('deposit.bankTransaction') || 0;
    const cashValueMinusChange = +cashValue - change

    const remainingPaymentsList = useMemo(() => {
        return periodsPaymentAnnuityDeal?.annuityPayments?.filter(
            p => p.annuityPaymentStatus !== StatusPaymentAnnuityPeriod.closed
        ) || [];
    }, [periodsPaymentAnnuityDeal]);

    const paidPaymentsList = useMemo(() => {
        return periodsPaymentAnnuityDeal?.annuityPayments?.filter(
            p => p.annuityPaymentStatus === StatusPaymentAnnuityPeriod.closed
        ) || [];
    }, [periodsPaymentAnnuityDeal]);

    const overduePayments = useMemo(() => {
        return remainingPaymentsList
            .filter(p => p.annuityPaymentStatus === StatusPaymentAnnuityPeriod.expired)
            .sort((a, b) => moment(a.dateFrom, 'DD.MM.YYYY') - moment(b.dateFrom, 'DD.MM.YYYY'));
    }, [remainingPaymentsList]);

    const isPaymentSelected = (paymentNumber) => {
        return selectedPayments.some(payment => payment.number === paymentNumber);
    };

    useEffect(() => {
        if (!isSuccessAnnuityPeriod) return;

        const hasFuturePayments = selectedPayments.some(
            el => el.status === StatusPaymentAnnuityPeriod.future
        );

        const generateFilename = (item: any) =>
            `exp_${item.dealNumber}_${item.startDate}_${item.endDate}.pdf`;

        const getOperationParams = (item: any, index: number) => ({
            dealId: item.id ?? item.dealId,
            dealType: item.type,
            cashBoxId: dataActiveCashbox.value,
            operationId: payPeriodAnnuity.operationIds[index]
        });

        const handlePrintedForms = () => {
            const isPrintingAddendum = hasFuturePayments ||
                +amountValue > +periodsPaymentAnnuityDeal?.sumToRecalculatePaymentPlan

            updateData();

            if (isPrintingAddendum) {
                item.forEach((currentItem, index) => {
                    const filename = generateFilename(currentItem);
                    const operationParams = getOperationParams(currentItem, index);

                    dispatch(getPrintedForm(
                        operationParams,
                        filename,
                        viewReports.ADDITIONAL_AGREEMENT
                    ));
                });
            } else {
                item.forEach((currentItem, index) => {
                    const filename = generateFilename(currentItem);
                    const operationParams = getOperationParams(currentItem, index);

                    dispatch(getPrintedForm(
                        operationParams,
                        filename,
                        viewReports.CLOSE_DEAL_RECEIPT
                    ));
                });
            }
        };

        handlePrintedForms();

        dispatch(changeStateAnnuityPeriod());
        setAmountValue(0);
        onClose();

    }, [isSuccessAnnuityPeriod, payPeriodAnnuity]);

    useEffect(() => {
        if (!dealId) return
        dispatch(getPaymentPeriodAnnuity(dealId))
    }, [dealId]);

    useEffect(() => {
        const totalSelectedAmount = selectedPayments.reduce((sum, payment) => {
            const originalPayment = remainingPaymentsList.find(p => p.number === payment.number);
            return sum + (originalPayment ? Number(originalPayment.paymentAmount) : 0);
        }, 0);
        setPaymentsValue(totalSelectedAmount);
    }, [selectedPayments, remainingPaymentsList]);

    useEffect(() => {
        if (isOpen && remainingPaymentsList.length > 0) {
            const overduePaymentObjects = overduePayments.map(p => ({
                number: p.number,
                status: p.annuityPaymentStatus
            }));
            setSelectedPayments(overduePaymentObjects);
        }
    }, [isOpen, remainingPaymentsList]);

    const getPaymentStatusLabel = (annuityPaymentStatus) => {
        switch (annuityPaymentStatus) {
            case StatusPaymentAnnuityPeriod.active:
                return {
                    text: 'Текущий период', className: 'payAnnuityPeriod-current'
                };
            case StatusPaymentAnnuityPeriod.expired:
                return {
                    text: 'Просрочен', className: 'payAnnuityPeriod-overdue'
                };
            default:
                return '';
        }
    };

    const handlePaymentSelection = (paymentId, isChecked) => {
        if (amountValue > 0) return;

        const paymentIndex = remainingPaymentsList.findIndex(p => p.number === paymentId);
        if (paymentIndex === -1) return;

        const oldestOverduePayment = overduePayments.length > 0
            ? overduePayments[0].number
            : null;

        if (!isChecked && paymentId === oldestOverduePayment) {
            return;
        }

        if (isChecked) {
            const paymentsToAdd = remainingPaymentsList
                .slice(0, paymentIndex + 1)
                .map(p => ({
                    number: p.number,
                    status: p.annuityPaymentStatus
                }));

            setSelectedPayments(prev => {
                const newPayments = [...prev];
                paymentsToAdd.forEach(paymentToAdd => {
                    if (!newPayments.some(p => p.number === paymentToAdd.number)) {
                        newPayments.push(paymentToAdd);
                    }
                });
                return newPayments;
            });
        } else {
            const paymentsToRemove = remainingPaymentsList
                .slice(paymentIndex)
                .map(p => p.number);

            setSelectedPayments(prev =>
                prev.filter(payment => !paymentsToRemove.includes(payment.number))
            );
        }
    };

    const isCheckboxDisabled = (payment) => {
        if (isPaymentSelected(payment.number)) return false;

        const paymentIndex = remainingPaymentsList.findIndex(p => p.number === payment.number);
        if (paymentIndex === 0) return false;

        const prevPayment = remainingPaymentsList[paymentIndex - 1];
        return !isPaymentSelected(prevPayment.number);
    };

    const submit = () => {
        const payload = {
            dealId: dealId,
            deposit: {
                cash: +cashValueMinusChange.toFixed(2),
                nonCash: +nonCashValue,
                nonCashAccount: +nonCashAccountValue,
                bankTransaction: +bankTransactionValue,
            }
        }

        dispatch(payAnnuityPeriod(payload))
    }

    const changeCalculationAmount = (e) => {
        const value = e.target.value.replace(',', '.');
        const numericValue = parseFloat(value);

        if (!isNaN(numericValue) && numericValue > 0) {
            setPaymentsValue(numericValue);
            setAmountValue(numericValue);

            if (overduePayments.length > 0) {
                setSelectedPayments([{
                    number: overduePayments[0].number,
                    status: overduePayments[0].annuityPaymentStatus
                }]);
            }
        } else {
            setPaymentsValue(0);
            setAmountValue(0);

            if (overduePayments.length > 0) {
                setSelectedPayments(overduePayments.map(p => ({
                    number: p.number,
                    status: p.annuityPaymentStatus
                })));
            }
        }
    };

    function sumPenalties(penaltiesArray, field) {
        if (!Array.isArray(penaltiesArray)) return 0;

        return penaltiesArray.reduce((total, penalty) => {
            return total + (penalty[field] || 0);
        }, 0);
    }

    const onCloseModal = () => setConfirmOpen(false);

    const onCloseHandler = () => setConfirmOpen(true);

    const getHandleAction = () => {
        onClose();
        setConfirmOpen(false);
        dispatch(clearModalError())
    };

    return (
        <>
            <ReactModal
                isOpen={isOpen}
                contentLabel="Modal"
                className="modal-container clientContract-modal-container payAnnuityPeriod"
                overlayClassName="modal-overlay"
            >
                <LoaderLinear isLoading={isModalLoading}/>
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(submit)}>
                        <h2>Внесение аннуитетного платежа по договору №{dealNumber}</h2>
                        {modalError && <Message type="error" text={modalError}/>}
                        <InfoPanel
                            panelKey="paidPayments"
                            title={`Внесено платежей (${paidPaymentsList.length})`}
                            rows={paidPaymentsList.map((payment) => ({
                                title: `${formatDate(payment.dateFrom)} - ${formatDate(payment.dateTo)}`,
                                value: payment.paymentAmount,
                            }))}
                        />
                        <InfoPanel
                            panelKey="remainingPayments"
                            title={`Осталось платежей (${remainingPaymentsList.length})`}
                            isShowRows={false}
                        >
                            {remainingPaymentsList.map((payment, idx) => {
                                const statusLabel = getPaymentStatusLabel(payment.annuityPaymentStatus);
                                const isDisabled = isCheckboxDisabled(payment);
                                const isChecked = isPaymentSelected(payment.number);

                                return (
                                    <div
                                        key={`payment-${idx}`}
                                        className='payAnnuityPeriod-remainingItem'
                                        style={{cursor: payment.annuityPaymentStatus === StatusPaymentAnnuityPeriod.expired ? 'pointer' : 'default'}}
                                    >
                                        <div className='payAnnuityPeriod-checkBox'>
                                            <HCheckbox
                                                checked={isChecked}
                                                onChange={(e) => handlePaymentSelection(payment.number, e.target.checked)}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                        <div
                                            className={`payAnnuityPeriod-paymentInfo ${statusLabel.className}`}>
                                            <InfoPanel
                                                headerClassName={statusLabel.className}
                                                extra={<span>{payment.paymentAmount} руб.</span>}
                                                title={`${formatDate(payment.dateFrom)} - ${formatDate(payment.dateTo)}`}
                                                rows={[
                                                    {title: 'Основной долг', value: payment.dealAmount},
                                                    {
                                                        title: 'Проценты за пользованием микрозаймом',
                                                        value: payment.overpaymentAmount
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </InfoPanel>
                        {
                            periodsPaymentAnnuityDeal?.annuityPenalties?.length > 0 && (
                                <InfoPanel
                                    panelKey="paidPayments"
                                    title={<span>
        Начисленная неустойка № <strong>{dealNumber}</strong> за {returnEndStr(sumPenalties(periodsPaymentAnnuityDeal?.annuityPenalties, 'days'))}
    </span>}
                                    extra={`${sumPenalties(periodsPaymentAnnuityDeal?.annuityPenalties, 'amount')} руб`}
                                    rows={periodsPaymentAnnuityDeal.annuityPenalties.map((payment) => ({
                                        title: `${formatDate(payment.dateFrom)} - ${formatDate(payment.dateTo)} за ${returnEndStr(payment?.days)}`,
                                        value: payment.amount
                                    }))}
                                />
                            )
                        }
                        <div
                            className="form-element flex-direction-row flex-space-between payAnnuityPeriod-amount">
                            <label
                                className="form-element-label m-vertical-auto"
                                htmlFor="amount">
                                Расчет от суммы:
                            </label>
                            <div className="form-element-data number">
                                <HInput
                                    h="true"
                                    type="text"
                                    name='calculationAmount'
                                    id='calculationAmount'
                                    className="input"
                                    decimals={2}
                                    onChange={changeCalculationAmount}
                                />
                            </div>
                        </div>
                        <PaymentTypeSelector
                            form={form}
                            maxTotalSum={amountValue ? amountValue : paymentsValue?.toFixed(2)}
                            calculateTotalSum={amountValue ? amountValue : paymentsValue}
                            inputFieldName={'deposit'}
                            setCashChangePayment={setChange}
                            setPaymentInputs={setPaymentInputs}
                        />
                        <div className="payAnnuityPeriod-actionButtons">
                            <button className="button-light" type="button" onClick={onCloseHandler}>
                                Отменить
                            </button>
                            {
                                permissionsAccess('accept.money') &&
                                <button className={disableSubmitButton ? "button-light" : "button"} type="submit"
                                        disabled={disableSubmitButton}>
                                    Принять деньги
                                </button>
                            }
                        </div>
                    </form>
                </FormProvider>
            </ReactModal>
            <ConfirmModal
                isOpen={confirmOpen}
                handleClose={onCloseModal}
                headerText={'Отменить внесение аннуитетного платежа?'}
                handleAction={getHandleAction}
            />
        </>
    );
};

export default PayAnnuityPeriod;